import type {
  Event as UserEvent,
  Entry as UserEntry,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { ActivityType } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import { useMemo } from "react"
import type { MangoQuery } from "rxdb"
import { useFollowedUsers } from "src/contexts"
import { useFindUserEntries } from "src/hooks/userEntry"
import { useFindUserEvents } from "src/hooks/userEvent"
import {
  createEntryQueryObjectForType,
  createEventQueryObjectForType,
} from "src/utils"

/*
 * this hook returns:
 * - the timestamp of the most recent glucose reading outside the normal range
 * - null if there is no such reading outside the normal range
 * - undefined if loading
 */
export function useLastOutsideOfNormalReadingTime(): string | undefined | null {
  const { currentFollow } = useFollowedUsers()

  const {
    range_normal_top: normalRangeTop,
    range_normal_bottom: normalRangeBottom,
  } = currentFollow.display_settings

  const accountId = currentFollow.following_account_id

  const outsideOfNormalEventQuery: MangoQuery<UserEvent> = useMemo(
    () => createEventQueryObjectForType(
      accountId,
      ActivityType.Glucose,
      {
        selector: {
          $or: [
            { [`${ActivityType.Glucose}.mg_dl`]: { $lt: normalRangeBottom } },
            { [`${ActivityType.Glucose}.mg_dl`]: { $gt: normalRangeTop } },
          ],
        },
        sort: [{ started_at: "desc" as const }],
        limit: 1,
      },
    ),
    [
      accountId,
      normalRangeTop,
      normalRangeBottom,
    ],
  )

  const outsideOfNormalEntryQuery: MangoQuery<UserEntry> = useMemo(
    () => createEntryQueryObjectForType(
      accountId,
      ActivityType.Glucose,
      {
        selector: {
          $or: [
            {
              subentries: {
                $elemMatch: {
                  [`${ActivityType.Glucose}.mg_dl`]: {
                    $lt: normalRangeBottom,
                  },
                },
              },
            },
            {
              subentries: {
                $elemMatch: {
                  [`${ActivityType.Glucose}.mg_dl`]: {
                    $gt: normalRangeTop,
                  },
                },
              },
            },
          ],
        },
        sort: [{ started_at: "desc" as const }],
        limit: 1,
      },
    ),
    [
      accountId,
      normalRangeTop,
      normalRangeBottom,
    ],
  )

  const {
    result: outsideOfNormalEventResult,
    isFetching: eventIsFetching,
  } = useFindUserEvents(outsideOfNormalEventQuery)

  const {
    result: outsideOfNormalEntryResult,
    isFetching: entryIsFetching,
  } = useFindUserEntries(outsideOfNormalEntryQuery)

  const lastOutsideOfNormalReadingTime = useMemo(
    () => {
      if (entryIsFetching || eventIsFetching) {
        return undefined
      }

      const outsideOfNormalEventResultStartedAt = outsideOfNormalEventResult[0]?.started_at
      const outsideOfNormalEntryResultStartedAt = outsideOfNormalEntryResult[0]?.started_at

      if (!outsideOfNormalEntryResultStartedAt && !outsideOfNormalEventResultStartedAt) {
        return null
      }

      if (!outsideOfNormalEntryResultStartedAt) {
        return outsideOfNormalEventResultStartedAt
      }

      if (!outsideOfNormalEventResultStartedAt) {
        return outsideOfNormalEntryResultStartedAt
      }

      if (dayjs(outsideOfNormalEntryResultStartedAt).isBefore(outsideOfNormalEventResultStartedAt)) {
        return outsideOfNormalEventResultStartedAt
      }

      return outsideOfNormalEntryResultStartedAt
    },
    [
      outsideOfNormalEventResult,
      outsideOfNormalEntryResult,
      entryIsFetching,
      eventIsFetching,
    ],
  )

  return lastOutsideOfNormalReadingTime
}
